<template lang="pug">
    .subscription-form.settings__inputs-wrap
        loading(v-if="isLoading")
        //- Новый каркас: тариф с одной золотой кнопкой на следующий уровень, кредиты, автопродление, история оплат
        template(v-else-if="$store.state.shellA")
            ._title {{ locale("planTitle") }}
            ._plan-box
                ._plan-head
                    ._plan-info
                        ._label {{ locale("plan") }}
                        ._plan {{ activePlan ? activePlan.name : "Free" }}
                        ._status(v-if="activePlan") {{ locale("activeUntil", { date: dueDate }) }}
                        ._status(v-else) {{ locale("free") }}
                    button._upgrade(v-if="nextLevel" type="button" @click="openPlans(nextLevel)") {{ locale("upgradeTo", { plan: planTitle(nextLevel) }) }}
                    button._change(v-else type="button" @click="openPlans()") {{ locale("change") }}
                ._teaser(v-if="nextLevel")
                    b {{ planTitle(nextLevel) }}
                    span {{ $t(`AISubscriptionsModal.tagline_${nextLevel}`) }}
            ._rows
                ._row
                    ._row-text
                        ._row-title {{ locale("credits") }}
                        ._row-hint {{ locale("creditsHint") }}
                    ._row-value {{ credits }}
                ._row(v-if="activePlan")
                    ._row-text
                        ._row-title {{ locale("renew") }}
                        ._row-hint(v-if="activePlan.auto_renew") {{ locale("nextPayment", { date: dueDate }) }}
                        ._row-hint(v-else) {{ locale("cancelled", { date: dueDate }) }}
                    button._row-action(v-if="activePlan.auto_renew" type="button" @click="openUnsubscribe") {{ locale("renewCancel") }}
            ._title {{ locale("history") }}
            ._history
                ._history-row(v-for="(p, i) in payments" :key="i")
                    ._history-date {{ paymentDate(p.created_at) }}
                    ._history-desc {{ describe(p) }}
                    ._history-sum {{ p.amount > 0 ? "+" + p.amount : Math.abs(p.amount) }}
                ._history-empty(v-if="!payments.length && !historyLoading")
                    template(v-if="historyError") {{ locale("historyError") }}
                    template(v-else)
                        ._history-empty-title {{ locale("historyEmpty") }}
                        ._history-empty-hint {{ locale("historyEmptyHint") }}
                button._history-more(v-if="historyHasMore" type="button" :disabled="historyLoading" @click="loadHistory") {{ locale("more") }}
        template(v-else)
            ._card
                ._label {{ locale("plan") }}
                ._plan {{ activePlan ? activePlan.name : "Free" }}
                ._status(v-if="activePlan && activePlan.auto_renew") {{ locale("nextPayment", { date: dueDate }) }}
                ._status(v-else-if="activePlan") {{ locale("cancelled", { date: dueDate }) }}
                ._status(v-else) {{ locale("free") }}
                button._primary(type="button" @click="$modal.show('AISubscriptions')") {{ activePlan ? locale("change") : locale("choose") }}
            //- Отмена доступна, но не на виду: текстовая ссылка внизу вкладки, как «Удалить аккаунт» на соседней
            button._cancel(v-if="activePlan && activePlan.auto_renew" type="button" @click="openUnsubscribe") {{ locale("unsubscribe") }}
</template>

<script>
import gql from "graphql-tag";
import getMySubscriptionsQuery from "@/graphql/queries/getMySubscriptions.query.graphql";
import paymentsListQuery from "@/graphql/queries/paymentsList.query.graphql";

// Тарифы «Оператора нейросетей», как в ai-subscriptions-modal и app-shell
const AI_PLAN_IDS = [26, 27, 32, 33, 34];
// Куда вести с текущего тарифа, как RECOMMENDED_LEVEL в ai-subscriptions-modal. С Platinum выше некуда
const NEXT_LEVEL = { FREE: "GOLD", PLUS: "GOLD", GOLD: "PLATINUM" };
const HISTORY_PAGE = 20;

export default {
    name: "SubscriptionSettingsForm",
    data: () => ({
        isLoading: true,
        mySubscriptions: [],
        payments: [],
        historyPage: 1,
        historyHasMore: false,
        historyLoading: false,
        historyError: false,
    }),
    computed: {
        activePlan() {
            return this.mySubscriptions.find((p) => AI_PLAN_IDS.includes(+p.id)) || null;
        },
        nextLevel() {
            return NEXT_LEVEL[this.activePlan ? this.activePlan.level : "FREE"] || null;
        },
        // Кредиты нейросетей: chatMessageAttempts, как в app-shell
        credits() {
            return Math.floor(Number((this.myUser || {}).chatMessageAttempts) || 0);
        },
        dueDate() {
            const date = this.activePlan?.subscribed_due?.date;
            return date ? this.formatDate(date.replace(" ", "T") + "Z", "dd.mm.yyyy") : "";
        },
    },
    mounted() {
        this.load();
        if (this.$store.state.shellA) this.loadHistory();
        this.$bus.$on("subscriptionChanged", this.load);
    },
    beforeDestroy() {
        this.$bus.$off("subscriptionChanged", this.load);
    },
    methods: {
        async load() {
            try {
                const r = await this.$apollo.query({ query: gql(getMySubscriptionsQuery), fetchPolicy: "no-cache" });
                this.mySubscriptions = r.data.getMySubscriptions;
            } catch (e) {
                console.log(e);
            }
            this.isLoading = false;
        },
        openUnsubscribe() {
            this.$modal.show("Unsubscribe", { mySubscriptions: this.mySubscriptions, planId: +this.activePlan.id });
        },
        openPlans(level) {
            this.$modal.show("AISubscriptions", level ? { level } : {});
        },
        planTitle(level) {
            return level.charAt(0) + level.slice(1).toLowerCase();
        },
        // Покупки из кошелька (вкладка PAYMENTS, как в окне вывода средств). Нулевые строки (проверки по тарифу) не показываем
        async loadHistory() {
            this.historyLoading = true;
            this.historyError = false;
            try {
                const r = await this.$apollo.query({
                    query: gql(paymentsListQuery),
                    variables: { tab: "PAYMENTS", page: this.historyPage, limit: HISTORY_PAGE },
                    fetchPolicy: "no-cache",
                });
                const page = r.data.paymentsList || {};
                this.payments.push(...(page.payments || []).filter((p) => p && Number(p.amount) !== 0));
                this.historyHasMore = !!page.hasMore;
                this.historyPage += 1;
            } catch (e) {
                this.historyError = true;
            }
            this.historyLoading = false;
        },
        paymentDate(value) {
            const date = new Date(value);
            return isNaN(date) ? value : this.formatDate(value, "dd.mm.yyyy");
        },
        describe(p) {
            const cat = p.category ? p.category.name : "";
            if (cat === "subscription") return this.locale("subscriptionPayment");
            const catKey = `purchaseDescriptions.category.${cat}`;
            if (!this.$te(catKey)) return cat;
            const sub = p.subcategory ? `purchaseDescriptions.subcategory.${p.subcategory.name}` : "";
            return this.$t(catKey) + (sub && this.$te(sub) ? " " + this.$t(sub) : "");
        },
    },
};
</script>

<style lang="scss">
@import "~@/styles/_variables.scss";

.subscription-form {
    &__card {
        width: 100%;
        max-width: 420px;
        padding: 20px;
        border: 1px solid #2f2f2f;
        border-radius: 16px;
        background: #1c1c1c;
    }

    &__label {
        font-size: 12px;
        color: #868586;
    }

    &__plan {
        margin-top: 4px;
        font-size: 22px;
        font-weight: 700;
        color: #fff;
    }

    &__status {
        margin-top: 6px;
        font-size: 14px;
        color: #a0a0a0;
    }

    &__primary {
        margin-top: 16px;
        padding: 10px 18px;
        border: 0;
        border-radius: 10px;
        background: $gold;
        color: #141414;
        font-weight: 600;
        cursor: pointer;
    }

    &__cancel {
        margin-top: 28px;
        padding: 0;
        border: 0;
        background: none;
        color: #868586;
        font-size: 13px;
        cursor: pointer;

        &:hover {
            color: #fff;
            text-decoration: underline;
        }
    }
}
</style>
